import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import {
  getAntdTheme,
  getThemePreference,
  resolveTheme,
  applyResolvedTheme,
  getSystemTheme,
  isFollowingSystem,
  persistThemePreference,
  resetToSystemPreference,
} from './tokens';

const ThemeContext = createContext(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

export default function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(getThemePreference);
  const [theme, setTheme] = useState(() => resolveTheme(getThemePreference()));

  useEffect(() => {
    persistThemePreference(preference);
    const resolved = applyResolvedTheme(preference);
    setTheme(resolved);
  }, [preference]);

  useEffect(() => {
    if (!isFollowingSystem(preference)) {
      return undefined;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = getSystemTheme();
      setTheme(resolved);
      applyResolvedTheme('system');
    };

    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [preference]);

  const isDark = theme === 'dark';
  const followingSystem = isFollowingSystem(preference);

  const toggleTheme = () => {
    setPreference(current => {
      const resolved = resolveTheme(current);
      return resolved === 'dark' ? 'light' : 'dark';
    });
  };

  const followSystem = () => {
    setPreference(resetToSystemPreference());
  };

  const antdConfig = useMemo(() => {
    const config = getAntdTheme(isDark);
    return {
      ...config,
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    };
  }, [isDark]);

  const value = useMemo(
    () => ({
      theme,
      preference,
      isDark,
      followingSystem,
      setPreference,
      setTheme: setPreference,
      toggleTheme,
      followSystem,
    }),
    [theme, preference, isDark, followingSystem],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}
